/**
 * Os blocos "Prestadores esporádicos" e "Férias" da coluna ao lado da grade.
 *
 * Papel: abre "Prestadores esporádicos" com a lista de prestadores cadastrados,
 * cada um com o "×", e o campo "Adicionar visitante..." com o "+"; abaixo,
 * lista as visitas e as ausências que atravessam o mês mostrado, cada uma com o
 * período `DD/MM/AAAA → DD/MM/AAAA` e os botões "✎" e "×", e abre o formulário
 * de cada lista ali mesmo, entre a lista e o botão "+ Incluir diárias" ou
 * "+ Adicionar férias". A diária escolhe o visitante entre os prestadores
 * cadastrados.
 *
 * Como usar: quem monta o painel passa os dois conjuntos já recortados pelo
 * mês e decide com `podeEditar` se aparecem os botões. Sem ele — o nível `ver`
 * —, as listas continuam legíveis, sem "✎", "×", campo nem botão de abrir
 * formulário. O "×" não apaga nada sozinho: chama `onRemoverPrestador`,
 * `onRemoverVisita` ou `onRemoverAusencia`, e a confirmação é de quem monta o
 * painel. Tirar um prestador da lista não mexe nas diárias dele.
 * `falhouAusencias` e `falhouVisitas` trocam a lista da seção por uma frase de
 * falha: sem elas, uma consulta que falhou apareceria como "Sem férias neste
 * mês", uma afirmação que ninguém chegou a conferir.
 *
 * Cada lista tem o seu formulário e os dois podem estar abertos juntos. O botão
 * de baixo alterna o formulário de criação: com ele aberto, o mesmo botão o
 * fecha. Um registro em edição que deixa a lista — removido pelo "×", ou fora do
 * mês depois de trocar de mês — fecha o formulário junto, em vez de deixá-lo
 * gravando num registro que não está mais à vista.
 *
 * Os períodos aparecem aqui e também nas células que cobrem, e as duas leituras
 * servem a perguntas diferentes: a célula responde "o que acontece neste dia", e
 * a lista responde "o que atravessa este mês".
 */
import { type Ausencia, type Prestador, type Visita } from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { formatarData } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import { useCadastrarPrestador } from '../hooks/usar-calendario';
import { type PessoaDaEscala } from '../types';
import { FormularioAusencia } from './formulario-ausencia';
import { FormularioVisita } from './formulario-visita';
import {
  BOTAO_DE_LINHA,
  LinhaDeAcrescentar,
  LinhaDoPainel,
  ListaDoPainel,
  SecaoDoPainel,
} from './partes-do-painel';

/** Se o formulário de uma lista está fechado, criando, ou editando um registro. */
type Formulario<T> = { aberto: false } | { aberto: true; registro: T | null };

const FECHADO = { aberto: false } as const;

interface PainelPeriodosProps {
  /** As ausências que encostam no mês mostrado. */
  ausencias: Ausencia[];
  /** As visitas que encostam no mês mostrado. */
  visitas: Visita[];
  /** Os prestadores cadastrados, na ordem em que entraram na lista. */
  prestadores: Prestador[];
  /** Quem está na escala da casa, que é quem pode ter férias. */
  pessoas: PessoaDaEscala[];
  /** A casa do calendário aberto, que recebe as visitas criadas aqui. */
  residenciaId: string;
  /** Se aparecem os botões de criar, editar e remover. */
  podeEditar: boolean;
  /** Se a consulta das ausências do mês falhou. */
  falhouAusencias?: boolean;
  /** Se a consulta das visitas do mês falhou. */
  falhouVisitas?: boolean;
  /** Se a consulta dos prestadores falhou. */
  falhouPrestadores?: boolean;
  onRemoverAusencia: (ausencia: Ausencia) => void;
  onRemoverVisita: (visita: Visita) => void;
  onRemoverPrestador: (prestador: Prestador) => void;
}

/**
 * Deixa só o formulário cujo registro ainda está na lista.
 *
 * @param formulario - O estado guardado do formulário.
 * @param lista - Os registros que a lista mostra agora.
 * @returns O mesmo estado, ou fechado quando o registro em edição saiu da lista.
 */
function emVigor<T extends { id: string }>(
  formulario: Formulario<T>,
  lista: readonly T[],
): Formulario<T> {
  if (!formulario.aberto || formulario.registro === null) return formulario;
  const { registro } = formulario;
  return lista.some((item) => item.id === registro.id) ? formulario : FECHADO;
}

/**
 * Ordena os períodos pelo primeiro dia, sem mexer na lista recebida.
 *
 * @param periodos - As visitas ou as ausências.
 * @returns Uma cópia, do período que começa antes para o que começa depois.
 */
function peloInicio<T extends { inicio: string }>(periodos: readonly T[]): T[] {
  return [...periodos].sort((a, b) => a.inicio.localeCompare(b.inicio));
}

/**
 * O período como a lista o escreve.
 *
 * @param periodo - O que tem `inicio` e `fim` em `AAAA-MM-DD`.
 * @returns As duas datas em `DD/MM/AAAA`, ligadas por uma seta.
 */
function comoSeLe(periodo: { inicio: string; fim: string }): string {
  return `${formatarData(periodo.inicio)} → ${formatarData(periodo.fim)}`;
}

/**
 * O nome e o período de uma linha, com a observação depois do nome.
 *
 * @param nome - Quem visita, ou quem está de férias.
 * @param observacao - O texto livre do registro, em cinza depois do nome.
 * @param periodo - As duas pontas do período.
 * @returns O bloco que ocupa o espaço livre da linha.
 */
function NomeEPeriodo({
  nome,
  observacao,
  periodo,
}: {
  nome: string;
  observacao: string | null;
  periodo: { inicio: string; fim: string };
}) {
  return (
    <div className="min-w-0 flex-1">
      <p className="font-semibold">
        {nome}
        {observacao !== null && (
          <span className="font-normal text-texto-fraco"> {observacao}</span>
        )}
      </p>
      <p className="mt-0.5 font-mono text-[10px] text-texto-fraco">{comoSeLe(periodo)}</p>
    </div>
  );
}

/**
 * Os dois blocos de períodos.
 *
 * @param ausencias - As ausências que encostam no mês mostrado.
 * @param visitas - As visitas que encostam no mês mostrado.
 * @param prestadores - Os prestadores cadastrados, oferecidos no formulário da
 *   diária.
 * @param pessoas - Quem está na escala da casa, oferecido no formulário de
 *   férias, que já abre na primeira pessoa.
 * @param residenciaId - A casa do calendário aberto.
 * @param podeEditar - Se aparecem os botões e os formulários.
 * @param falhouAusencias - Verdadeiro quando a consulta das ausências falhou; a
 *   seção "Férias" mostra a falha no lugar da lista.
 * @param falhouVisitas - Verdadeiro quando a consulta das visitas falhou; a
 *   seção "Prestadores esporádicos" mostra a falha no lugar da lista.
 * @param falhouPrestadores - Verdadeiro quando a consulta dos prestadores
 *   falhou; a lista deles mostra a falha no lugar dos nomes.
 * @param onRemoverAusencia - Chamado pelo "×" de uma ausência.
 * @param onRemoverVisita - Chamado pelo "×" de uma visita.
 * @param onRemoverPrestador - Chamado pelo "×" de um prestador.
 * @returns As duas seções, uma abaixo da outra no fluxo da coluna.
 */
export function PainelPeriodos({
  ausencias,
  visitas,
  prestadores,
  pessoas,
  residenciaId,
  podeEditar,
  falhouAusencias = false,
  falhouVisitas = false,
  falhouPrestadores = false,
  onRemoverAusencia,
  onRemoverVisita,
  onRemoverPrestador,
}: PainelPeriodosProps) {
  const cadastrar = useCadastrarPrestador();
  const [guardadoDeVisita, setFormularioDeVisita] = useState<Formulario<Visita>>(FECHADO);
  const [guardadoDeAusencia, setFormularioDeAusencia] =
    useState<Formulario<Ausencia>>(FECHADO);

  const formularioDeVisita = emVigor(guardadoDeVisita, visitas);
  const formularioDeAusencia = emVigor(guardadoDeAusencia, ausencias);

  /** Cadastra o prestador digitado, ou diz por que não cadastrou. */
  async function handleCadastrarPrestador(nome: string): Promise<string | null> {
    if (prestadores.some((p) => p.nome === nome)) return 'Visitante já cadastrado';
    try {
      await cadastrar.mutateAsync({ residenciaId, nome });
      return null;
    } catch (erro) {
      return erro instanceof ErroDaApi
        ? erro.message
        : 'O cadastro falhou antes de chegar ao servidor.';
    }
  }

  return (
    <>
      <SecaoDoPainel titulo="Prestadores esporádicos">
        <ListaDoPainel
          vazia={prestadores.length === 0}
          mensagemVazia="Nenhum visitante cadastrado"
          erro={falhouPrestadores ? 'Não foi possível carregar os prestadores.' : undefined}
        >
          {prestadores.map((prestador) => (
            <LinhaDoPainel key={prestador.id}>
              <span className="flex-1 font-medium">{prestador.nome}</span>
              {podeEditar && (
                <Button
                  type="button"
                  size="xs"
                  variant="destructive"
                  aria-label={`Remover ${prestador.nome} da lista de visitantes`}
                  className={BOTAO_DE_LINHA}
                  onClick={() => onRemoverPrestador(prestador)}
                >
                  ×
                </Button>
              )}
            </LinhaDoPainel>
          ))}
        </ListaDoPainel>
        {podeEditar && (
          <LinhaDeAcrescentar
            placeholder="Adicionar visitante..."
            onAcrescentar={handleCadastrarPrestador}
          />
        )}

        <ListaDoPainel
          vazia={visitas.length === 0}
          mensagemVazia="Sem diárias neste mês"
          erro={falhouVisitas ? 'Não foi possível carregar as diárias do mês.' : undefined}
        >
          {peloInicio(visitas).map((visita) => (
            <LinhaDoPainel key={visita.id} alinharNoTopo>
              <NomeEPeriodo
                nome={visita.visitante}
                observacao={visita.observacao}
                periodo={visita}
              />
              {podeEditar && (
                <>
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    aria-label={`Editar a diária de ${visita.visitante}`}
                    className={BOTAO_DE_LINHA}
                    onClick={() => setFormularioDeVisita({ aberto: true, registro: visita })}
                  >
                    ✎
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    variant="destructive"
                    aria-label={`Remover a diária de ${visita.visitante}`}
                    className={BOTAO_DE_LINHA}
                    onClick={() => onRemoverVisita(visita)}
                  >
                    ×
                  </Button>
                </>
              )}
            </LinhaDoPainel>
          ))}
        </ListaDoPainel>

        {podeEditar && formularioDeVisita.aberto && (
          <FormularioVisita
            key={formularioDeVisita.registro?.id ?? 'nova'}
            visita={formularioDeVisita.registro}
            residenciaId={residenciaId}
            prestadores={prestadores.map((p) => p.nome)}
            onSalvo={() => setFormularioDeVisita(FECHADO)}
            onCancelar={() => setFormularioDeVisita(FECHADO)}
          />
        )}
        {podeEditar && (
          <Button
            type="button"
            variant="outline"
            aria-expanded={formularioDeVisita.aberto}
            className="mb-3.5 w-full justify-start"
            onClick={() =>
              setFormularioDeVisita(
                formularioDeVisita.aberto ? FECHADO : { aberto: true, registro: null },
              )
            }
          >
            + Incluir diárias
          </Button>
        )}
      </SecaoDoPainel>

      <SecaoDoPainel titulo="Férias">
        <ListaDoPainel
          vazia={ausencias.length === 0}
          mensagemVazia="Sem férias neste mês"
          erro={falhouAusencias ? 'Não foi possível carregar as férias do mês.' : undefined}
        >
          {peloInicio(ausencias).map((ausencia) => {
            const nome = ausencia.colaborador.apelido ?? ausencia.colaborador.nomeCompleto;
            return (
              <LinhaDoPainel key={ausencia.id} alinharNoTopo>
                <NomeEPeriodo
                  nome={nome}
                  observacao={ausencia.observacao}
                  periodo={ausencia}
                />
                {podeEditar && (
                  <>
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      aria-label={`Editar as férias de ${nome}`}
                      className={BOTAO_DE_LINHA}
                      onClick={() =>
                        setFormularioDeAusencia({ aberto: true, registro: ausencia })
                      }
                    >
                      ✎
                    </Button>
                    <Button
                      type="button"
                      size="xs"
                      variant="destructive"
                      aria-label={`Remover as férias de ${nome}`}
                      className={BOTAO_DE_LINHA}
                      onClick={() => onRemoverAusencia(ausencia)}
                    >
                      ×
                    </Button>
                  </>
                )}
              </LinhaDoPainel>
            );
          })}
        </ListaDoPainel>

        {podeEditar && formularioDeAusencia.aberto && (
          <FormularioAusencia
            key={formularioDeAusencia.registro?.id ?? 'nova'}
            ausencia={formularioDeAusencia.registro}
            pessoas={pessoas}
            onSalvo={() => setFormularioDeAusencia(FECHADO)}
            onCancelar={() => setFormularioDeAusencia(FECHADO)}
          />
        )}
        {podeEditar && (
          <Button
            type="button"
            variant="outline"
            aria-expanded={formularioDeAusencia.aberto}
            className="mb-3.5 w-full justify-start"
            onClick={() =>
              setFormularioDeAusencia(
                formularioDeAusencia.aberto ? FECHADO : { aberto: true, registro: null },
              )
            }
          >
            + Adicionar férias
          </Button>
        )}
      </SecaoDoPainel>
    </>
  );
}
